/**
 * BiblePanel — v3.46.8 (25 May 2026)
 *
 * The Complete Source-of-Truth Bible export.
 *
 * One button. Regenerates a single .docx from /app/memory/*.md on every
 * press — the markdown source-of-truth is the canon, this button just
 * freezes a snapshot of it into a Word doc the founder can download,
 * mail, archive, or vault (Fort Knox · the "Documents" piece).
 *
 * Because the regen is on-demand and live, the bible is always current
 * — there is no manual sync, no stale copies. Update a spec markdown,
 * the next button press picks it up.
 *
 * The downloaded filename carries a UTC timestamp + git short SHA, so
 * every export is a traceable snapshot of a precise codebase state.
 */
import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";

const BIBLE_DOC_LIST = [
    { name: "SOURCE_OF_TRUTH_INDEX.md", role: "The map — read this first" },
    { name: "ROADMAP.md", role: "What to build next, why, in what order" },
    { name: "LIFESTATE_ARCHITECTURE_SPEC.md", role: "23 pillars + 4 memory layers" },
    { name: "MEMBERSHIP_ARCHITECTURE_SPEC.md", role: "Phase 3 KEYSTONE — everything sits on this" },
    { name: "SELF_ENRICHING_SYSTEM_SPEC.md", role: "Part A (supplier network) + Part B (the Soul / moat)" },
    { name: "DELIGHT_LAYER_SPEC.md", role: "D-α / β / γ / δ — beloved, not just competent" },
    { name: "PRACTICAL_FORESIGHT_NOTE.md", role: "D-ε — anticipation, official sources only" },
    { name: "PRIVATE_CLUB_ACCESS_FLOW_SPEC.md", role: "BUILT · template for all programme benefits" },
    { name: "ROADMAP_v1_FOUNDER_SOURCE.md", role: "Founder's pristine v1.0 archive" },
];

export default function BiblePanel() {
    const [downloading, setDownloading] = useState(false);
    const [lastDownload, setLastDownload] = useState(null);
    const [error, setError] = useState("");

    async function downloadBible() {
        setDownloading(true);
        setError("");
        try {
            const response = await api.get("/admin/bible/export", {
                responseType: "blob",
            });

            // Pull filename from Content-Disposition (server-stamped with
            // UTC timestamp + git SHA — preserve it).
            const cd = response.headers["content-disposition"] || "";
            const m = cd.match(/filename="([^"]+)"/);
            const filename = m ? m[1] : `Les_Concierges_Mira_Bible_${new Date().toISOString().slice(0, 10)}.docx`;

            // Browser download
            const blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            setLastDownload({
                filename,
                size: blob.size,
                generatedAt: response.headers["x-bible-generated-at"] || new Date().toISOString(),
            });
        } catch (e) {
            setError(formatApiError(e) || "Could not generate the bible — check backend logs.");
        } finally {
            setDownloading(false);
        }
    }

    return (
        <div data-testid="bible-panel" className="max-w-3xl">
            <div className="mb-8">
                <p className="text-[10px] tracking-[0.28em] uppercase text-[hsl(var(--brass))] mb-3">
                    THE COMPLETE BIBLE
                </p>
                <h2 className="text-[28px] font-serif text-[hsl(var(--navy))] leading-tight mb-3">
                    Source-of-truth, in one Word document
                </h2>
                <p className="text-[14px] leading-[1.7] text-[hsl(var(--muted))] italic">
                    Every founding spec · every locked decision · every open Phase 3/4 question.
                    Regenerates from <code className="not-italic text-[hsl(var(--navy))]">/app/memory/</code> on
                    every press — the markdown is the canon; this Word doc is a snapshot of it at the
                    moment of export. Update a spec and the next press picks it up. No manual sync,
                    no stale copies.
                </p>
            </div>

            <div className="mb-8 p-6 border border-[hsl(var(--brass))]/30 bg-[hsl(var(--brass))]/5 rounded">
                <div className="flex items-start justify-between gap-6">
                    <div className="flex-1">
                        <p className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--muted))] mb-2">
                            DOWNLOAD
                        </p>
                        <h3 className="text-[18px] font-serif text-[hsl(var(--navy))] mb-2">
                            Export the current bible
                        </h3>
                        <p className="text-[13px] text-[hsl(var(--slate,_#4A5A6A))] leading-relaxed">
                            One Word document · {BIBLE_DOC_LIST.length} living specs · cover page with
                            UTC timestamp and git commit SHA · ready to mail or vault.
                        </p>
                    </div>
                    <button
                        onClick={downloadBible}
                        disabled={downloading}
                        data-testid="bible-download-btn"
                        className="shrink-0 px-6 py-3 bg-[hsl(var(--navy))] text-white text-[12px] tracking-[0.2em] uppercase hover:bg-[hsl(var(--brass))] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    >
                        {downloading ? "Composing…" : "Export bible (.docx)"}
                    </button>
                </div>

                {error && (
                    <p data-testid="bible-error" className="mt-4 text-[12px] text-red-700 italic">
                        {error}
                    </p>
                )}

                {lastDownload && (
                    <div data-testid="bible-last-download" className="mt-5 pt-4 border-t border-[hsl(var(--brass))]/20 text-[11px] text-[hsl(var(--muted))] italic">
                        ✓ Last export · <span className="font-medium not-italic text-[hsl(var(--navy))]">{lastDownload.filename}</span>
                        &nbsp;·&nbsp;{Math.round(lastDownload.size / 1024)} KB
                        &nbsp;·&nbsp;generated {new Date(lastDownload.generatedAt).toLocaleString()}
                    </div>
                )}
            </div>

            <div className="mb-8">
                <p className="text-[10px] tracking-[0.28em] uppercase text-[hsl(var(--muted))] mb-4">
                    WHAT'S INSIDE
                </p>
                <ol className="space-y-3">
                    {BIBLE_DOC_LIST.map((d, idx) => (
                        <li key={d.name} className="flex items-baseline gap-4 text-[13px]">
                            <span className="shrink-0 text-[hsl(var(--brass))] font-medium w-6">
                                {idx + 1}.
                            </span>
                            <div className="flex-1 min-w-0">
                                <code className="text-[12px] text-[hsl(var(--navy))]">{d.name}</code>
                                <p className="text-[12px] text-[hsl(var(--muted))] italic mt-0.5">
                                    {d.role}
                                </p>
                            </div>
                        </li>
                    ))}
                </ol>
            </div>

            <div className="text-[11px] text-[hsl(var(--muted))] italic leading-relaxed pt-6 border-t border-[hsl(var(--border))]">
                <strong className="not-italic text-[hsl(var(--navy))]">Fort Knox vault:</strong> this
                button is the "Documents" surface. Tomorrow it pairs with a 4 AM cron that auto-mails
                the freshest bible to your Google Drive — so the canon lives in your vault, not just
                in the Emergent environment.
            </div>
        </div>
    );
}
